import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Helpers from "../Config/Helpers";
import { useNavigate } from "react-router-dom";
import iziToast from "izitoast";
import "izitoast/dist/css/iziToast.min.css";
import {
  Box,
  Paper,
  Stack,
  TextField,
  Typography,
  IconButton,
  Chip,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import PaymentIcon from "@mui/icons-material/Payment";
import HomeIcon from "@mui/icons-material/Home";
import PageShell from "./ModuleLayout/PageShell";
import ReusableDataGrid from "../Components/ReusableDataGrid";

function UnpaidReceipts() {
  const [search, setSearch] = useState("");
  const [receipts, setUnpaidReceipts] = useState([]);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  const getUnpaidReceipts = useCallback(() => {
    setLoading(true);
    axios
      .get(
        `${Helpers.baseUrl}/receipts/unpaid/${Helpers.authParentId}`,
        Helpers.headers,
      )
      .then((response) => setUnpaidReceipts(response.data.reverse()))
      .catch((error) => Helpers.unauthenticated(error, navigate))
      .finally(() => setLoading(false));
  }, [navigate]);

  const handleDelete = (receiptId) => {
    iziToast.warning({
      timeout: 20000,
      close: false,
      overlay: true,
      displayMode: "once",
      zindex: 9999,
      title: "",
      message: "Are you sure to delete this receipt",
      position: "center",
      buttons: [
        [
          "<button><b>YES</b></button>",
          function (instance, toast) {
            axios
              .get(
                `${Helpers.baseUrl}/receipts/delete/${receiptId}`,
                Helpers.headers,
              )
              .then(() => {
                getUnpaidReceipts();
                instance.hide({ transitionOut: "fadeOut" }, toast, "button");
                Helpers.toast("success", "Receipt deleted successfully");
              })
              .catch((error) => {
                Helpers.unauthenticated(error, navigate);
                instance.hide({ transitionOut: "fadeOut" }, toast, "button");
              });
          },
          true,
        ],
        [
          "<button>NO</button>",
          function (instance, toast) {
            instance.hide({ transitionOut: "fadeOut" }, toast, "button");
          },
        ],
      ],
    });
  };

  useEffect(() => {
    getUnpaidReceipts();
  }, [getUnpaidReceipts]);

  const money = (n) => {
    const v = Number(n);
    return Number.isFinite(v) ? `$ ${v.toFixed(2)}` : "$ 0.00";
  };

  const totals = receipts.reduce(
    (acc, r) => {
      acc.total += Number(r?.totalPrice) || 0;
      acc.paid += Number(r?.paid) || 0;
      acc.remaining += Number(r?.remaining) || 0;
      return acc;
    },
    { total: 0, paid: 0, remaining: 0 },
  );

  const filteredReceipts = receipts.filter((receipt) => {
    const term = search.trim().toLowerCase();
    if (!term) return true;
    const customerName = receipt?.customer?.name?.toLowerCase() || "";
    const vehicleName =
      `${receipt?.vehicle?.make || ""} ${receipt?.vehicle?.model || ""}`.toLowerCase();
    const status = receipt?.status?.toLowerCase() || "";
    return (
      customerName.includes(term) ||
      vehicleName.includes(term) ||
      status.includes(term)
    );
  });

  const statusColor = (status) => {
    if (status === "Paid") return "success";
    if (status === "Unpaid") return "error";
    return "warning";
  };

  const columns = [
    { field: "serial", headerName: "Sr. #", width: 70, sortable: false },
    {
      field: "customer",
      headerName: "Customer",
      flex: 1,
      minWidth: 150,
      valueGetter: (_, row) => row.customer?.name || "",
    },
    {
      field: "vehicle",
      headerName: "Vehicle",
      flex: 1,
      minWidth: 160,
      valueGetter: (_, row) =>
        row.vehicle
          ? `${row.vehicle.make || ""} ${row.vehicle.model || ""}`.trim()
          : "",
    },
    {
      field: "totalPrice",
      headerName: "Total",
      width: 110,
      renderCell: ({ row }) => money(row.totalPrice),
    },
    {
      field: "paid",
      headerName: "Paid",
      width: 110,
      renderCell: ({ row }) => money(row.paid),
    },
    {
      field: "remaining",
      headerName: "Remaining",
      width: 120,
      renderCell: ({ row }) => (
        <span
          style={{
            color: Number(row.remaining) > 0 ? "#d32f2f" : "#2e7d32",
            fontWeight: 600,
          }}
        >
          {money(row.remaining)}
        </span>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      renderCell: ({ row }) => (
        <Chip
          label={row.status || "—"}
          color={statusColor(row.status)}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 130,
      sortable: false,
      renderCell: ({ row }) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            height: "100%",
          }}
        >
          <IconButton
            size="small"
            color="warning"
            title="Make Payment"
            onClick={() =>
              navigate(`/user/unpaid-receipt/make-payment/${row._id}`)
            }
          >
            <PaymentIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="success"
            title="View receipt"
            onClick={() =>
              navigate(`/user/receipts/receipt-details/${row._id}`)
            }
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            title="Delete receipt"
            onClick={() => handleDelete(row._id)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const rows = filteredReceipts.map((r, i) => ({
    ...r,
    id: r._id,
    serial: i + 1,
  }));

  return (
    <PageShell
      title="Unpaid Receipts"
      description="All invoices with an outstanding balance"
      icon={require("../images/icons/receipt.png")}
      breadcrumbs={[
        {
          label: "Home",
          path: "/user/dashboard",
          icon: <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />,
        },
        { label: "Receipts", path: "/user/receipts" },
        { label: "Unpaid", path: null },
      ]}
      summaryChips={[
        { label: "Total Unpaid", value: money(totals.remaining) },
        ...(filteredReceipts.length > 0
          ? [
              {
                label: `${filteredReceipts.length} receipt${filteredReceipts.length !== 1 ? "s" : ""}`,
              },
            ]
          : []),
      ]}
      variant="list"
    >
      <Paper
        elevation={1}
        sx={{
          borderRadius: 2,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          minHeight: 0,
          mb: 2,
        }}
      >
        {/* ── Toolbar ── */}
        <Box
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: "divider",
            background: "linear-gradient(to right, #f8f9fa 0%, #ffffff 100%)",
            flexShrink: 0,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              Unpaid Receipts
            </Typography>

            <TextField
              label="Search"
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 220 }}
            />
          </Stack>
        </Box>

        {/* ── Grid ── */}
        <Box
          sx={{
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <ReusableDataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            pageSize={10}
            pageSizeOptions={[10, 25, 50, 100]}
            noRowsMessage="No unpaid receipts found"
          />
        </Box>
      </Paper>
    </PageShell>
  );
}

export default UnpaidReceipts;
